import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import { FilterField } from '@totem/components/ui_components/reportSettings/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import MyWorkContext from '@totem/components/users/myWork/MyWorkContext';
import { MyWorkItem } from '@totem/components/users/types';
import { USER_ENDPOINT } from '@totem/utilities/endpoints';

// Sort/filter/paging are applied client-side by MyWorkTable; InputContainer
// holds that state and persists it per user (shared by the My Work page and
// the Portfolio panel, which show the same list).
const FILTER_CONFIG: FilterField[] = [
  {
    key: 'objectType',
    label: 'Type',
    sortField: 'objectType',
    filterField: 'objectType',
    multiple: true,
    values: [],
  },
];

type Props = {
  children?: ReactNode;
};

const MyWorkContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [data, setData] = useState<MyWorkItem[]>([]);

  useEffect(() => {
    if (!refreshData) {
      return;
    }
    setRefreshData(false);

    setLoading(true);

    fetch(`${USER_ENDPOINT}/myWork`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: MyWorkItem[]) => {
        setData(Array.isArray(result) ? result : []);
      })
      .then(() => {
        setLoading(false);
      });
  }, [refreshData, userProfile.organization.id, getApiToken]);

  useEffect(() => {
    setRefreshData(true);
  }, [userProfile.organization.id]);

  const handleRefresh = useCallback(() => {
    setRefreshData(true);
  }, []);

  return (
    <MyWorkContext.Provider
      value={{
        loading,
        data,
        totalRecords: data.length,
        onRefresh: handleRefresh,
      }}
    >
      <InputContainer
        componentKey="my-work"
        componentLabel="My Work"
        filterConfig={FILTER_CONFIG}
        defaultPageSize={20}
      >
        {children}
      </InputContainer>
    </MyWorkContext.Provider>
  );
};

export default MyWorkContainer;
